import { useQuery } from '@tanstack/react-query'
import { chatBuilderService } from '@/services/chatBuilder'
import { useAuthStore } from '@/stores/auth'

// Query keys for configurations - includes organization context for proper cache separation
export const configurationKeys = {
  all: (orgId?: string) => ['configurations', orgId] as const,
  lists: (orgId?: string) => [...configurationKeys.all(orgId), 'list'] as const,
  list: (
    orgId?: string,
    params?: { status?: string; page?: number; perPage?: number }
  ) => [...configurationKeys.lists(orgId), params] as const,
  details: (orgId?: string) =>
    [...configurationKeys.all(orgId), 'detail'] as const,
  detail: (id: string, orgId?: string) =>
    [...configurationKeys.details(orgId), id] as const,
}

interface UseConfigurationsOptions {
  status?: string
  page?: number
  perPage?: number
  enabled?: boolean
}

/**
 * Hook for fetching all chat configurations with React Query caching
 * Provides automatic background refetching and cache management
 */
export function useConfigurations(options: UseConfigurationsOptions = {}) {
  const { organization } = useAuthStore()
  const { status, page, perPage, enabled = true } = options

  const queryKey = configurationKeys.list(organization?.id, {
    status,
    page,
    perPage,
  })

  const { data, isLoading, error, refetch, isFetching, isRefetching } =
    useQuery({
      queryKey,
      queryFn: () =>
        chatBuilderService.getConfigurations({ status, page, perPage }),
      enabled: enabled && !!organization?.id,
      staleTime: 2 * 60 * 1000, // 2 minutes - data is fresh for 2 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch when internet reconnects
    })

  // Extract configurations array from response
  const configurations = data?.configurations || []

  // Filter configurations by status for convenience
  const published = configurations.filter(
    (config) => config.status === 'published'
  )
  const draft = configurations.filter((config) => config.status === 'draft')
  const inactive = configurations.filter(
    (config) => config.status === 'inactive'
  )

  return {
    // Data
    configurations,
    published,
    draft,
    inactive,
    total: data?.total || 0,
    page: data?.page || 1,
    perPage: data?.perPage || 25,

    // Loading states
    isLoading, // First time loading
    isFetching, // Loading or background refetching
    isRefetching, // Background refetching only

    // Error
    error,

    // Actions
    refetch,
  }
}

/**
 * Hook for fetching all configurations without pagination
 * Useful for dropdowns, settings, etc.
 */
export function useAllConfigurations() {
  return useConfigurations({
    perPage: 1000, // Large number to get all configs
  })
}
